"use client"

import styled from "@emotion/styled"
import { Box, Breadcrumbs } from "@mui/material"
import Link from "next/link"
import { usePathname } from "next/navigation"
import React from "react"
import { NewText } from "./NewText/NewText"
import { BreadcrumbIcon } from "./icons"
import { sharedColors } from "./theme/colors"

const StyledContainer = styled(Box)`
  padding: 12px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 12px 0;
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
`
const Seperator = styled.div<{ en: boolean }>`
  ${({ en }) => en && `transform: scale(-1);`}
`
const StyledCurrentText = styled(NewText)`
  ${(props) => props.theme.breakpoints.down("md")} {
    text-overflow: ellipsis;
    max-width: 114px;
    white-space: nowrap;
    overflow: hidden;
  }
`
type BreadcrumbType = {
  title: string | JSX.Element
  url: string
  noClick?: boolean
  isPage?: boolean
}

type Props = {
  current: BreadcrumbType
  parents: BreadcrumbType[]
  children?: JSX.Element
  locale?: string,
  firstItemIconText?: boolean
  firstItemIconTextUnderline?: boolean
  firstIconText?: string
  loadSchema?: boolean
}
// Get origin from environment variable
const ORIGIN = process.env.ORIGIN || ""
export const CustomBreadcrumbs: React.FC<Props> = ({
  current,
  parents,
  children,
  locale,
  firstItemIconText = false,
  firstItemIconTextUnderline = false,
  firstIconText,
  // loadSchema = true,
}) => {
  const pathname = usePathname()
  // UI pages logic: always include first parent (homepage) + any parents explicitly marked as pages
  const pages = React.useMemo(() => {
    if (!parents || parents.length === 0) return [] as BreadcrumbType[]
    const first = parents[0]
    const rest = parents.slice(1).filter((p) => p && p.isPage)
    return [first, ...rest]
  }, [parents])

  // Helper: normalize string title for schema (React nodes can't be serialized)
  const resolveTitle = (item: BreadcrumbType | undefined): string => {
    if (!item) return ""
    if (typeof item.title === "string") return item.title
    // Fallbacks: use provided firstIconText (if applied to first item) or locale-based home label
    if (item === parents[0]) {
      if (firstIconText && typeof firstIconText === "string") return firstIconText
      return locale === "en" ? "Home" : "דף הבית"
    }
    // Generic fallback for non-string dynamic titles
    return locale === "en" ? "Page" : "עמוד"
  }

  // Helper to make URLs absolute (schema.org recommends absolute URLs)
  const runtimeOrigin = React.useMemo(() => {
    if (ORIGIN) return ORIGIN.replace(/\/$/, "")
    if (typeof window !== "undefined" && window.location?.origin) return window.location.origin
    return ""
  }, [])

  const makeAbsoluteUrl = (url: string) => {
    if (!url) return ""
    if (/^https?:\/\//i.test(url)) return url
    if (!runtimeOrigin) return url // fallback to relative if origin not provided (SSR phase)
    return `${runtimeOrigin}${url.startsWith("/") ? url : `/${url}`}`
  }

  // Build schema list mirroring UI order + current page at the end
  const breadcrumbList = React.useMemo(() => {
    const structuralItems: { "@type": "ListItem"; position: number; name: string; item?: string }[] = []
    let position = 1
    pages.forEach((p) => {
      if (!p) return
      const name = resolveTitle(p).trim()
      if (!name) return
      const url = makeAbsoluteUrl(p.url)
      structuralItems.push({
        "@type": "ListItem",
        position: position++,
        name,
        ...(url && { item: url }),
      })
    })
    // Append current page (even if it's already in pages; typically it's not)
    if (current) {
      const resolvedCurrentUrl = (current.url && current.url !== "/") ? current.url : (pathname || "/")
      const currentName = resolveTitle(current).trim() || (typeof current.title === "string" ? current.title : (locale === "en" ? "Current Page" : "עמוד נוכחי"))
      if (currentName) {
        const currentUrl = makeAbsoluteUrl(resolvedCurrentUrl)
        structuralItems.push({
          "@type": "ListItem",
          position: position++,
          name: currentName,
          ...(currentUrl && { item: currentUrl }),
        })
      }
    }
    return structuralItems
  }, [pages, current, locale, firstIconText, pathname])

  // const schemaMarkup = React.useMemo(() => ({
  //   "@context": "https://schema.org",
  //   "@type": "BreadcrumbList",
  //   itemListElement: breadcrumbList,
  // }), [breadcrumbList])
  return (
    <>
      {/* {loadSchema && (
        <script
          id="breadcrumb-schema"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }}
        // strategy="beforeInteractive"
        />
      )} */}
      <StyledContainer>
        {children}

        <Breadcrumbs
          role="navigation"
          separator={<BreadcrumbIcon en={locale === "en"} aria-hidden="true" />}
          aria-label="המיקום שלך באתר"
        >
          {
            pages.map((item, index) => {
              // TODO: find dynamic solution for parents with no actual page
              if (!item) { return null }
              if ((!item.noClick && item.isPage) || index === 0) {
                return (
                  <StyledLink href={item.url} key={index}>
                    {typeof item.title !== "string" ? (
                      <div style={{ fontSize: 0, display: "flex", gap: 2 }} aria-label="דף הבית">{item.title}</div>
                    ) : (
                      <>
                        <NewText
                          component="p"
                          desktopFontSize={12}
                          desktopFontWeight="400"
                          desktopLineHeight="150%"
                          color={sharedColors.blue}
                        >
                          {item.title}
                        </NewText>

                        {firstItemIconText && <NewText
                          component="p"
                          desktopFontSize={12}
                          desktopFontWeight="400"
                          desktopLineHeight="150%"
                          color={sharedColors.blue}
                          style={{ textDecoration: firstItemIconTextUnderline ? "underline" : "none" }}
                        >
                          {firstIconText}
                        </NewText>}
                      </>
                    )
                      // : (
                      //   <NewText
                      //     component="p"
                      //     desktopFontSize={12}
                      //     desktopFontWeight="400"
                      //     desktopLineHeight="150%"
                      //     color={sharedColors.blue}
                      //   >
                      //     {item.title}
                      //   </NewText>
                      // )
                    }
                  </StyledLink>
                )
              } else {
                return (
                  <NewText
                    component="p"
                    desktopFontSize={12}
                    desktopFontWeight="500"
                    desktopLineHeight="150%"
                    color={sharedColors.gray}
                    key={index}
                  >
                    {item.title}
                  </NewText>
                )
              }
            })}
          <StyledCurrentText
            component="p"
            desktopFontSize={12}
            desktopFontWeight="500"
            desktopLineHeight="150%"
            color={sharedColors.gray}
          >
            {current.title}
          </StyledCurrentText>
        </Breadcrumbs>
      </StyledContainer>
    </>
  )
}

export default CustomBreadcrumbs
